import { useState, useEffect } from "react";
import { PortalChrome, HealthPill, StatusPill, MOCK, useRole } from "./_shared";
import {
  Plug, Package, Inbox, Receipt, AlertTriangle, BarChart3,
  Search, Filter, ChevronRight, Users, Check, Lock, ExternalLink,
  DollarSign, Zap, CheckCircle2,
  Plus, Info, Layers, Sparkles,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────
// Dropship redesign — FINAL preview (combined direction)
//   • Left rail from Option 4: "All buyers" aggregate + per-buyer drill-in
//   • Right pane keeps the CURRENT 6 tabs (Setup · Catalog · Daily orders
//     · Auto-invoices · Claims · Analytics)
//   • Setup wraps: agreement (one-time), catalog mirror-from-Catalog-app,
//     per-buyer pricing overrides & dropship fees, and (buyer only) their
//     outbound channels (Shopify, Woo, Amazon, Walmart, TikTok, CSV, Webhook)
//   • All content adapts based on "All" vs a specific partner
// ─────────────────────────────────────────────────────────────────────────

const SELLER_TABS = [
  { id: "setup",     label: "Setup",         icon: Plug },
  { id: "catalog",   label: "Catalog Sync",  icon: Package },
  { id: "daily",     label: "Daily Orders",  icon: Inbox, badge: 2 },
  { id: "invoices",  label: "Auto-Invoices", icon: Receipt },
  { id: "claims",    label: "Claims",        icon: AlertTriangle, badge: 2 },
  { id: "analytics", label: "Analytics",     icon: BarChart3 },
];

const BUYER_TABS = [
  { id: "setup",     label: "Setup",         icon: Plug },
  { id: "catalog",   label: "Catalog",       icon: Package },
  { id: "daily",     label: "Daily Orders",  icon: Inbox, badge: 2 },
  { id: "invoices",  label: "Auto-Invoices", icon: Receipt },
  { id: "claims",    label: "Claims",        icon: AlertTriangle, badge: 1 },
  { id: "analytics", label: "Analytics",     icon: BarChart3 },
];

// mock channels (buyer's outbound)
const BUYER_CHANNELS = [
  { id: "shopify", name: "Shopify",       status: "connected",   sync: "Real-time · 2m ago", icon: "🅢" },
  { id: "woo",     name: "WooCommerce",   status: "connected",   sync: "5m ago",             icon: "W" },
  { id: "amazon",  name: "Amazon SP-API", status: "action",      sync: "Auth token expiring", icon: "A" },
  { id: "walmart", name: "Walmart",       status: "disconnected", icon: "W" },
  { id: "tiktok",  name: "TikTok Shop",   status: "disconnected", icon: "T" },
  { id: "csv",     name: "CSV drop",      status: "disconnected", icon: "CSV" },
  { id: "webhook", name: "Custom webhook", status: "disconnected", icon: "{ }" },
];

export default function DropshipFinal() {
  const [role, setRole] = useRole();
  const m = MOCK[role];
  const isSeller = role === "seller";
  const [selectedId, setSelectedId] = useState("all"); // "all" or partner id
  const [tab, setTab] = useState("daily");

  const isAll = selectedId === "all";
  const selected = isAll ? null : m.partners.find(p => p.id === selectedId);
  // Reset selection when role changes and stale id doesn't exist in new role.
  useEffect(() => {
    if (!isAll && !selected) setSelectedId("all");
  }, [selected, isAll]);
  const tabs = isSeller ? SELLER_TABS : BUYER_TABS;

  const displayPartner = isAll || !selected
    ? { name: `All ${m.partnersLabel.toLowerCase()}`, sub: `${m.partners.length} active · aggregate view` }
    : { name: selected.name, sub: `${isSeller ? selected.channel + " · " : ""}${selected.sync}` };

  return (
    <PortalChrome role={role} setRole={setRole} optionLabel="Final direction · workspace-split + current tabs" optionNumber="★">
      {/* Page header (matches PortalShell.PageHeader) */}
      <div className="border-b border-zinc-200 bg-white px-6 lg:px-8 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Dropship</h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            {isSeller ? "Mirror your catalog to buyer channels · agreement-first" : "Mirror supplier catalogs to your channels · agreement-first"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="text-xs font-medium px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50 inline-flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> {isSeller ? "Invite buyer" : "Find supplier"}
          </button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* LEFT rail — partners list (Option 4 style) */}
        <aside className="w-72 flex-shrink-0 border-r border-zinc-200 bg-white flex flex-col">
          <div className="p-4 border-b border-zinc-100">
            <div className="flex items-center gap-2 bg-zinc-50 ring-1 ring-zinc-200 rounded-md px-2.5 py-1.5">
              <Search className="w-3.5 h-3.5 text-zinc-400" />
              <input placeholder={`Search ${m.partnersLabel.toLowerCase()}…`} className="text-xs bg-transparent flex-1 outline-none" />
              <Filter className="w-3.5 h-3.5 text-zinc-400" />
            </div>
            <div className="flex items-center gap-1 mt-3">
              <FilterChip label="All"      count={m.partners.length} active />
              <FilterChip label="Needs me" count={2} tone="amber" />
              <FilterChip label="Live"     count={m.partners.filter(p => p.health === "ok").length} />
            </div>
          </div>

          {/* Aggregate row — pinned to top */}
          <button
            onClick={() => setSelectedId("all")}
            className={`w-full text-left px-4 py-3 border-b border-zinc-100 hover:bg-zinc-50/60 transition ${isAll ? "bg-zinc-900/[0.03] border-l-2 border-l-zinc-900" : "border-l-2 border-l-transparent"}`}
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-zinc-900 text-white flex items-center justify-center flex-shrink-0">
                <Users className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">All {m.partnersLabel.toLowerCase()}</span>
                  <span className="text-[10px] text-zinc-400">aggregate</span>
                </div>
                <div className="text-[11px] text-zinc-500 mt-0.5 tabular-nums">
                  {m.partners.reduce((s, p) => s + p.orders30d, 0)} orders · ${m.partners.reduce((s, p) => s + p.gmv30d, 0).toLocaleString()} / 30d
                </div>
              </div>
            </div>
          </button>

          <div className="flex-1 overflow-y-auto">
            <div className="px-4 pt-3 pb-2 text-[10px] uppercase tracking-widest text-zinc-400">Individual {m.partnersLabel.toLowerCase()}</div>
            {m.partners.map(p => {
              const isSelected = selectedId === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => setSelectedId(p.id)}
                  className={`w-full text-left px-4 py-3 border-b border-zinc-100 hover:bg-zinc-50/60 transition ${isSelected ? "bg-zinc-900/[0.03] border-l-2 border-l-zinc-900" : "border-l-2 border-l-transparent"}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-zinc-100 flex items-center justify-center text-xs font-semibold text-zinc-700">
                      {p.name.split(" ").map(w => w[0]).slice(0, 2).join("")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium truncate">{p.name}</span>
                        {!p.mapOk && <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[11px] text-zinc-500 tabular-nums">${p.gmv30d.toLocaleString()}</span>
                        <span className="text-zinc-300">·</span>
                        <HealthPill kind={p.health}>{p.sync}</HealthPill>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="p-3 border-t border-zinc-100 text-[11px] text-zinc-500 flex items-center justify-between">
            <span>{m.partners.length} active</span>
            <span className="tabular-nums">${m.partners.reduce((s, p) => s + p.gmv30d, 0).toLocaleString()} · 30d</span>
          </div>
        </aside>

        {/* RIGHT: workspace with 6 tabs */}
        <main className="flex-1 overflow-y-auto bg-zinc-50">
          {/* Workspace header */}
          <div className="bg-white border-b border-zinc-200 px-8 pt-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-xs text-zinc-500 mb-1">
                  {isAll ? "Aggregate view" : `${m.partnerLabel} workspace`}
                </div>
                <h2 className="text-2xl font-semibold tracking-tight">{displayPartner.name}</h2>
                <div className="flex items-center gap-2 mt-2">
                  {isAll ? (
                    <>
                      <HealthPill kind="ok">{m.partners.filter(p => p.health === "ok").length} healthy</HealthPill>
                      {m.partners.some(p => p.health === "warn")  && <HealthPill kind="warn">1 warning</HealthPill>}
                      {m.partners.some(p => p.health === "error") && <HealthPill kind="error">1 error</HealthPill>}
                    </>
                  ) : (
                    <>
                      <HealthPill kind={selected.health}>Synced · {selected.sync}</HealthPill>
                      <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-zinc-100 inline-flex items-center gap-1"><Lock className="w-3 h-3" />Agreement locked</span>
                      {isSeller && <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-zinc-100">{selected.channel}</span>}
                    </>
                  )}
                </div>
              </div>
              {!isAll && (
                <div className="flex items-center gap-2">
                  <button className="text-xs font-medium px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50">Message</button>
                  <button className="text-xs font-medium px-3 py-1.5 rounded-md bg-zinc-900 text-white">Force resync</button>
                </div>
              )}
            </div>

            {/* Mini KPI strip */}
            <div className="grid grid-cols-4 gap-6 mt-5">
              <KPI label="Orders 30d" value={isAll ? m.partners.reduce((s, p) => s + p.orders30d, 0) : selected.orders30d} />
              <KPI label="GMV 30d"    value={`$${(isAll ? m.partners.reduce((s, p) => s + p.gmv30d, 0) : selected.gmv30d).toLocaleString()}`} />
              <KPI label="Credit"     value={isAll ? "$7.1K / $33K" : selected.credit} />
              <KPI label="MAP"        value={isAll
                ? (m.partners.every(p => p.mapOk) ? <span className="text-emerald-700">Clean</span> : <span className="text-amber-600">1 flag</span>)
                : (selected.mapOk ? <span className="text-emerald-700">Clean</span> : <span className="text-amber-600">1 flag</span>)} />
            </div>

            {/* 6 tabs bar */}
            <div className="flex gap-1 mt-5 -mb-px overflow-x-auto">
              {tabs.map(t => {
                const Icon = t.icon;
                const isActive = tab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    className={`text-[13px] font-medium px-3.5 py-2.5 border-b-2 transition inline-flex items-center gap-1.5 whitespace-nowrap ${isActive ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {t.label}
                    {t.badge && (
                      <span className={`ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${isActive ? "bg-zinc-900 text-white" : "bg-amber-100 text-amber-800"}`}>{t.badge}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab content */}
          <div className="p-8">
            {tab === "setup"     && <SetupTab     role={role} m={m} isAll={isAll} selected={selected} />}
            {tab === "catalog"   && <CatalogTab   role={role} m={m} isAll={isAll} selected={selected} />}
            {tab === "daily"     && <OrdersTab    role={role} m={m} isAll={isAll} selected={selected} />}
            {tab === "invoices"  && <InvoicesTab  role={role} m={m} isAll={isAll} selected={selected} />}
            {tab === "claims"    && <ClaimsTab    role={role} m={m} isAll={isAll} selected={selected} />}
            {tab === "analytics" && <AnalyticsTab role={role} m={m} isAll={isAll} selected={selected} />}
          </div>
        </main>
      </div>
    </PortalChrome>
  );
}

// ─── Setup ───────────────────────────────────────────────────────────────
function SetupTab({ role, isAll, selected }) {
  const isSeller = role === "seller";
  return (
    <div className="max-w-4xl space-y-4">
      {isAll && (
        <InfoBanner>
          Setup shown here is your <b>global default</b>. Select a specific {isSeller ? "buyer" : "supplier"} on the left to override pricing, fees, or channels for that relationship.
        </InfoBanner>
      )}

      {/* Step 1 — Agreement (only when a specific partner is selected) */}
      {!isAll && (
        <SetupCard
          n={1}
          title="Agreement"
          done
          collapsedSummary="Net 30 · $1.20 handling · MAP strict · Locked Jan 12, 2026"
        >
          <div className="grid grid-cols-2 gap-y-2.5 text-sm">
            <div className="text-zinc-500">Payment terms</div>     <div className="font-medium">Net 30</div>
            <div className="text-zinc-500">Handling fee</div>      <div className="font-medium">$1.20 / order</div>
            <div className="text-zinc-500">MAP enforcement</div>   <div className="font-medium">Strict · auto-flagged</div>
            <div className="text-zinc-500">Returns window</div>    <div className="font-medium">14 days</div>
            <div className="text-zinc-500">Damaged shipments</div> <div className="font-medium">100% credit + reship (photo &lt; 7d)</div>
          </div>
          <div className="mt-4 pt-4 border-t border-zinc-100 flex items-center gap-2">
            <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-zinc-100 inline-flex items-center gap-1"><Lock className="w-3 h-3" />Locked</span>
            <button className="text-xs font-medium text-zinc-900 hover:underline">Propose amendment</button>
          </div>
        </SetupCard>
      )}

      {/* Step 2 — Catalog source (mirrored from Catalog app) */}
      <SetupCard
        n={isAll ? 1 : 2}
        title="Catalog source"
        done
        icon={Layers}
        collapsedSummary="Mirrored from Catalog app · 27 SKUs live"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-zinc-100 flex items-center justify-center flex-shrink-0">
            <Package className="w-5 h-5 text-zinc-500" />
          </div>
          <div className="flex-1 text-sm">
            <div className="font-medium">Your Catalog app is the source of truth</div>
            <p className="text-zinc-500 mt-1">
              {isSeller
                ? "Products, images, MSRP, and MAP live in the Catalog app. Everything here mirrors from there — no duplicate management. Shopify import stays in Catalog."
                : "Your mirrored view of the supplier catalog lives in your Catalog app. Add markup rules there, then choose what to expose to your channels below."}
            </p>
            <button className="mt-3 text-xs font-medium text-zinc-900 hover:underline inline-flex items-center gap-1">
              Open Catalog app <ExternalLink className="w-3 h-3" />
            </button>
          </div>
        </div>
      </SetupCard>

      {/* Step 3 — Pricing & fees (per-buyer overrides) */}
      <SetupCard
        n={isAll ? 2 : 3}
        title={isSeller ? "Pricing & dropship fees" : "Markup & storefront pricing"}
        icon={DollarSign}
        collapsedSummary={
          isAll
            ? (isSeller ? "Global defaults · same price for all buyers" : "Global markup · +30% over wholesale")
            : (isSeller ? "Same as global · $1.20 handling · no overrides" : "Custom markup · +45% over wholesale")
        }
      >
        {isSeller ? (
          <div className="space-y-4">
            <RadioRow
              checked
              title="Same pricing for all buyers (recommended)"
              body="Wholesale, MAP and handling from Catalog app apply to everyone."
            />
            <RadioRow
              title={isAll ? "Set per-buyer overrides when you pick a buyer on the left" : "Override for this buyer only"}
              body={isAll ? "Choose a specific buyer to define custom wholesale, MAP, or handling for them." : "Custom wholesale, MAP, and handling that only apply to this buyer."}
              disabled={isAll}
            />

            <div className="border-t border-zinc-100 pt-4 mt-4">
              <div className="text-xs font-semibold text-zinc-700 mb-3">Dropship fees</div>
              <div className="grid grid-cols-3 gap-3">
                <FeeInput label="Handling fee"   value="1.20" unit="$/order" />
                <FeeInput label="Rush handling"  value="2.50" unit="$/order" />
                <FeeInput label="Insurance"      value="0.60" unit="$/order" />
              </div>
              {!isAll && <div className="text-[11px] text-zinc-500 mt-2">These apply <b>only</b> to {selected.name}. Blank fields inherit the global default.</div>}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <RadioRow
              checked
              title="Same markup for all suppliers"
              body="Applies a flat +30% over wholesale on any product you list."
            />
            <RadioRow
              title={isAll ? "Set per-supplier markup when you pick one on the left" : "Custom markup for this supplier"}
              body={isAll ? "Choose a specific supplier to define a different markup or MAP floor." : "Override the global markup for products from this supplier."}
              disabled={isAll}
            />
            <div className="border-t border-zinc-100 pt-4 mt-4">
              <div className="text-xs font-semibold text-zinc-700 mb-3">Storefront rules</div>
              <div className="grid grid-cols-3 gap-3">
                <FeeInput label="Markup %"       value="30"   unit="%" />
                <FeeInput label="Min margin"     value="20"   unit="%" />
                <FeeInput label="Round to"       value=".99"  unit="$" />
              </div>
            </div>
          </div>
        )}
      </SetupCard>

      {/* Step 4 (buyer only) — Outbound channels */}
      {!isSeller && (
        <SetupCard
          n={isAll ? 3 : 4}
          title="Push to my sales channels"
          icon={Zap}
          collapsedSummary="2 connected · 5 available"
        >
          <p className="text-sm text-zinc-500 mb-4">
            Connect a channel and this dropship catalog will sync <b>out</b> automatically. Orders coming <b>in</b> flow back through Verqo to your supplier — no manual forwarding.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {BUYER_CHANNELS.map(c => <ChannelCard key={c.id} channel={c} />)}
          </div>
          <InfoBanner tone="dark" className="mt-4">
            <span><b>How this works:</b> Verqo owns the catalog. Your channel calls verqo for prices & inventory. When a customer buys, the order lands in Verqo → routes to the supplier → supplier ships → tracking auto-pushes back to your channel.</span>
          </InfoBanner>
        </SetupCard>
      )}
    </div>
  );
}

// ─── Catalog Sync ────────────────────────────────────────────────────────
function CatalogTab({ role, m, isAll, selected }) {
  const isSeller = role === "seller";
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-widest text-zinc-500">
            {isSeller
              ? (isAll ? "Catalog mirrored to all buyers" : `Catalog mirrored to ${selected.name}`)
              : (isAll ? "Available from all suppliers" : `Available from ${selected.name}`)}
          </h3>
          <p className="text-xs text-zinc-500 mt-1">
            {isSeller
              ? "Mirrored from your Catalog app. Per-buyer pricing shown when you pick a buyer on the left."
              : "Mirrored from your Catalog app. Add to any connected channel with one click."}
          </p>
        </div>
        <button className="text-xs font-medium px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50 inline-flex items-center gap-1.5">
          <ExternalLink className="w-3 h-3" /> Open Catalog app
        </button>
      </div>

      <div className="rounded-2xl bg-white border border-zinc-200/80 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50/70 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left font-medium px-4 py-2.5">SKU</th>
              <th className="text-left font-medium px-4 py-2.5">Product</th>
              {isSeller ? (
                <>
                  <th className="text-right font-medium px-4 py-2.5">MSRP</th>
                  <th className="text-right font-medium px-4 py-2.5">MAP</th>
                  <th className="text-right font-medium px-4 py-2.5">Wholesale{!isAll && <span className="ml-1 text-emerald-600" title="Overridden for this buyer">•</span>}</th>
                  <th className="text-right font-medium px-4 py-2.5">Handling</th>
                  <th className="text-right font-medium px-4 py-2.5">{isAll ? "Mirrored" : "Status"}</th>
                </>
              ) : (
                <>
                  <th className="text-left  font-medium px-4 py-2.5">Supplier</th>
                  <th className="text-right font-medium px-4 py-2.5">Wholesale</th>
                  <th className="text-right font-medium px-4 py-2.5">Your price</th>
                  <th className="text-right font-medium px-4 py-2.5">Margin</th>
                  <th className="text-right font-medium px-4 py-2.5">Channels</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {m.catalog.slice(0, 6).map(p => (
              <tr key={p.sku} className="border-t border-zinc-100 hover:bg-zinc-50/60">
                <td className="px-4 py-3 font-mono text-xs">{p.sku}</td>
                <td className="px-4 py-3">{p.name}</td>
                {isSeller ? (
                  <>
                    <td className="px-4 py-3 text-right tabular-nums">${p.msrp}</td>
                    <td className="px-4 py-3 text-right tabular-nums">${p.map}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {!isAll ? <span className="text-emerald-700 font-medium">${(p.wholesale - 0.5).toFixed(2)}</span> : <span>${p.wholesale}</span>}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">${p.handling}</td>
                    <td className="px-4 py-3 text-right">
                      {isAll
                        ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-100">{p.mirrored} buyers</span>
                        : <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Live</span>}
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-3 text-zinc-700">{p.supplier}</td>
                    <td className="px-4 py-3 text-right tabular-nums">${p.wholesale}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium">${(p.wholesale * 1.45).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-medium">{Math.round(p.margin * 100)}%</td>
                    <td className="px-4 py-3 text-right">
                      {p.inStore
                        ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">2 channels</span>
                        : <button className="text-[11px] font-medium text-zinc-900 hover:underline">Add</button>}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isSeller && !isAll && (
        <InfoBanner>
          <span className="text-emerald-600">•</span> indicates a per-buyer pricing override. Managed in <b>Setup → Pricing & dropship fees</b>.
        </InfoBanner>
      )}
    </div>
  );
}

// ─── Daily Orders ────────────────────────────────────────────────────────
function OrdersTab({ role, m, isAll, selected }) {
  const orders = isAll ? m.orders : m.orders.filter(o => o.partner === selected.name);
  const [chip, setChip] = useState("all");
  const chips = ["all", "action", "transit", "done", "stall", "claim"];
  const chipLabels = { all: "All", action: "Action needed", transit: "In transit", done: "Delivered", stall: "Stalled", claim: "Claims" };
  const chipFilter = { action: "Action needed", transit: "In transit", done: "Delivered", stall: "Stalled", claim: "Claim" };
  const filtered = chip === "all" ? orders : orders.filter(o => o.status === chipFilter[chip]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {chips.map(c => (
          <button
            key={c}
            onClick={() => setChip(c)}
            className={`text-[11px] font-medium px-3 py-1.5 rounded-full ring-1 transition ${chip === c ? "bg-zinc-900 text-white ring-zinc-900" : "bg-white text-zinc-700 ring-zinc-200 hover:ring-zinc-300"}`}
          >
            {chipLabels[c]}
          </button>
        ))}
        <div className="ml-auto text-xs text-zinc-500">{orders.length} orders</div>
      </div>

      <div className="rounded-2xl bg-white border border-zinc-200/80 overflow-hidden">
        {filtered.length === 0 && (
          <div className="p-10 text-center text-sm text-zinc-500">No orders match this filter.</div>
        )}
        {filtered.map((o, i) => (
          <div key={o.id} className={`px-5 py-3.5 flex items-center gap-3 hover:bg-zinc-50/60 ${i > 0 ? "border-t border-zinc-100" : ""}`}>
            <span className="font-mono text-[10px] text-zinc-500 w-20">{o.id}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{o.customer}</div>
              <div className="text-[11px] text-zinc-500 truncate">{o.partner} · {o.note}</div>
            </div>
            <span className="tabular-nums text-sm font-medium">${o.total.toFixed(2)}</span>
            <StatusPill status={o.status} />
            {o.sla && <span className="text-[10px] font-medium text-amber-700 bg-amber-50 ring-1 ring-amber-200 px-2 py-0.5 rounded-full">{o.sla}</span>}
            <ChevronRight className="w-3.5 h-3.5 text-zinc-300" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Auto-Invoices ───────────────────────────────────────────────────────
function InvoicesTab({ role, m, isAll, selected }) {
  const isSeller = role === "seller";
  const invoices = [
    { id: "INV-3341", partner: "Daisy & Co Boutique", period: "Nov 24 – Dec 1", total: 4123.40, status: "Paid",      auto: "Auto-debited" },
    { id: "INV-3342", partner: "Northside Pharmacy",  period: "Dec 1 – Dec 8",  total: 2810.10, status: "Paid",      auto: "Auto-debited" },
    { id: "INV-3343", partner: "Urban Tots",          period: "Dec 8 – Dec 15", total:  982.30, status: "Due Dec 15", auto: "Scheduled" },
    { id: "INV-3344", partner: "Coastal Mercantile",  period: "Dec 8 – Dec 15", total:  341.00, status: "Overdue",   auto: "Retry queued" },
  ];
  const rows = isAll ? invoices : invoices.filter(i => i.partner === selected.name);

  return (
    <div className="space-y-4">
      <InfoBanner>
        {isSeller
          ? "Invoices auto-post to each buyer at the cadence set in their agreement. Payments auto-debit from their wallet or credit line."
          : "Invoices post automatically from each supplier at the cadence in their agreement. Payments debit from your wallet or credit line — no manual action needed."}
      </InfoBanner>

      <div className="rounded-2xl bg-white border border-zinc-200/80 overflow-hidden">
        {rows.length === 0 && <div className="p-10 text-center text-sm text-zinc-500">No invoices for {selected?.name || "this window"}.</div>}
        {rows.map((inv, i) => (
          <div key={inv.id} className={`px-5 py-3.5 flex items-center gap-3 hover:bg-zinc-50/60 ${i > 0 ? "border-t border-zinc-100" : ""}`}>
            <Receipt className="w-4 h-4 text-zinc-400" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{inv.id}</div>
              <div className="text-[11px] text-zinc-500">{inv.partner} · {inv.period}</div>
            </div>
            <span className="tabular-nums text-sm font-medium">${inv.total.toFixed(2)}</span>
            <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${inv.status === "Paid" ? "bg-emerald-100 text-emerald-700" : inv.status === "Overdue" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>{inv.status}</span>
            <span className="text-[10px] text-zinc-500 w-24 text-right">{inv.auto}</span>
            <ChevronRight className="w-3.5 h-3.5 text-zinc-300" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Claims ──────────────────────────────────────────────────────────────
function ClaimsTab({ m, isAll, selected }) {
  const claims = [
    { id: "CL-902", partner: "Coastal Mercantile", reason: "Damaged on arrival",  status: "Refund pending", age: "1d" },
    { id: "CL-901", partner: "Urban Tots",         reason: "No carrier movement", status: "Investigating",  age: "2d" },
  ];
  const rows = isAll ? claims : claims.filter(c => c.partner === selected.name);

  return (
    <div className="space-y-2">
      {rows.length === 0 && (
        <div className="rounded-2xl bg-white border border-zinc-200/80 p-10 text-center">
          <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
          <div className="text-sm font-medium">No open claims for {selected?.name || "this window"}</div>
          <div className="text-xs text-zinc-500 mt-1">Claims open automatically when MAP is violated or tracking stalls 48h+</div>
        </div>
      )}
      {rows.map(c => (
        <div key={c.id} className="rounded-2xl bg-white border border-zinc-200/80 p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">{c.id} · {c.reason}</div>
            <div className="text-xs text-zinc-500">{c.partner} · {c.age} open</div>
          </div>
          <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{c.status}</span>
          <button className="text-xs font-medium px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50">Open</button>
        </div>
      ))}
    </div>
  );
}

// ─── Analytics ───────────────────────────────────────────────────────────
function AnalyticsTab({ m, isAll, selected }) {
  const gmv = isAll ? m.partners.reduce((s, p) => s + p.gmv30d, 0) : selected.gmv30d;
  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-white border border-zinc-200/80 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm font-semibold">GMV · last 30 days</div>
            <div className="text-2xl font-semibold tabular-nums mt-0.5">${gmv.toLocaleString()}</div>
            <div className="text-xs text-emerald-600 mt-0.5">+18% vs prior period</div>
          </div>
        </div>
        <div className="flex items-end gap-1 h-24">
          {[40, 55, 42, 60, 48, 72, 58, 65, 70, 82, 75, 88, 78, 90, 82, 96, 88, 100, 94, 108, 100, 112, 105, 118, 110, 124, 118, 130, 125, 138].map((h, i) => (
            <div key={i} className="flex-1 rounded-sm" style={{ height: `${h * 0.65}%`, background: `linear-gradient(180deg, rgb(16 185 129 / ${0.4 + (i / 30) * 0.5}), rgb(16 185 129 / 0.15))` }}></div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <StatTile label="Fulfillment rate"  value="97%"       hint="+2 pts vs prev" />
        <StatTile label="Avg order value"   value="$119"      hint="+$8 vs prev" />
        <StatTile label="On-time delivery"  value="94%"       hint="-1 pt vs prev" accent="warn" />
      </div>
    </div>
  );
}

// ─── Building blocks ─────────────────────────────────────────────────────
function SetupCard({ n, title, icon: Icon, done, collapsedSummary, children }) {
  const [open, setOpen] = useState(!done);
  return (
    <div className="rounded-2xl bg-white border border-zinc-200/80 overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full px-5 py-4 flex items-center gap-3 hover:bg-zinc-50/60 transition">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${done ? "bg-emerald-100 text-emerald-700" : "bg-zinc-900 text-white"}`}>
          {done ? <Check className="w-4 h-4" /> : (Icon ? <Icon className="w-4 h-4" /> : <span className="text-xs font-bold">{n}</span>)}
        </div>
        <div className="flex-1 min-w-0 text-left">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">{n}. {title}</span>
            {done && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100">Done</span>}
          </div>
          {!open && collapsedSummary && <div className="text-xs text-zinc-500 mt-0.5 truncate">{collapsedSummary}</div>}
        </div>
        <ChevronRight className={`w-4 h-4 text-zinc-400 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5 border-t border-zinc-100 pt-4">{children}</div>}
    </div>
  );
}

function RadioRow({ checked, disabled, title, body }) {
  return (
    <div className={`rounded-lg border p-3 flex items-start gap-3 ${checked ? "border-zinc-900 bg-zinc-900/[0.02]" : "border-zinc-200"} ${disabled ? "opacity-50" : "cursor-pointer hover:border-zinc-400"}`}>
      <div className={`w-4 h-4 rounded-full border-2 mt-0.5 flex-shrink-0 flex items-center justify-center ${checked ? "border-zinc-900" : "border-zinc-300"}`}>
        {checked && <div className="w-1.5 h-1.5 rounded-full bg-zinc-900"></div>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-zinc-500 mt-0.5">{body}</div>
      </div>
    </div>
  );
}

function FeeInput({ label, value, unit }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</label>
      <div className="mt-1 flex items-center rounded-md ring-1 ring-zinc-200 focus-within:ring-zinc-900 bg-white">
        <input defaultValue={value} className="flex-1 px-3 py-1.5 text-sm outline-none rounded-l-md tabular-nums" />
        <span className="px-2.5 text-xs text-zinc-500 border-l border-zinc-200 py-1.5">{unit}</span>
      </div>
    </div>
  );
}

function ChannelCard({ channel }) {
  const isConnected = channel.status === "connected";
  const isAction = channel.status === "action";
  return (
    <div className={`rounded-lg border p-3 flex items-center gap-3 ${isConnected ? "border-zinc-200 bg-white" : isAction ? "border-amber-200 bg-amber-50/50" : "border-zinc-200 bg-white hover:border-zinc-400 transition"}`}>
      <div className="w-9 h-9 rounded-md bg-zinc-100 flex items-center justify-center text-sm font-bold text-zinc-700 flex-shrink-0">
        {channel.icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{channel.name}</div>
        <div className="text-[11px] text-zinc-500 truncate">
          {isConnected ? channel.sync : isAction ? channel.sync : "Not connected"}
        </div>
      </div>
      {isConnected && <HealthPill kind="ok">Live</HealthPill>}
      {isAction && <HealthPill kind="warn">Action</HealthPill>}
      {!isConnected && !isAction && <button className="text-[11px] font-medium text-zinc-900 hover:underline">Connect</button>}
    </div>
  );
}

function KPI({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-lg font-semibold tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function StatTile({ label, value, hint, accent }) {
  return (
    <div className="rounded-2xl bg-white border border-zinc-200/80 p-4">
      <div className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-xl font-semibold tracking-tight mt-1 ${accent === "warn" ? "text-amber-600" : "text-zinc-900"}`}>{value}</div>
      {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

function FilterChip({ label, count, active, tone }) {
  const cls = active
    ? "bg-zinc-900 text-white"
    : tone === "amber"
      ? "bg-amber-50 text-amber-700 ring-1 ring-amber-200"
      : "bg-zinc-50 text-zinc-700 ring-1 ring-zinc-200";
  return (
    <button className={`text-[11px] font-medium px-2 py-1 rounded-md ${cls}`}>
      {label} <span className="opacity-70 tabular-nums">{count}</span>
    </button>
  );
}

function InfoBanner({ tone = "light", className = "", children }) {
  const cls = tone === "dark"
    ? "bg-zinc-900 text-zinc-100 border-zinc-900"
    : "bg-blue-50/70 text-zinc-700 border-blue-100";
  return (
    <div className={`rounded-lg border ${cls} p-3.5 flex items-start gap-2.5 text-xs ${className}`}>
      {tone === "dark"
        ? <Sparkles className="w-3.5 h-3.5 text-amber-300 mt-0.5 flex-shrink-0" />
        : <Info className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" />}
      <div className="flex-1">{children}</div>
    </div>
  );
}
